import { useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useAdminOrgs } from '../features/admin/hooks/useAdminOrgs';
import type { OrgWithMembers, OrgSettingsInput } from '../features/admin/types';
import CreateOrgModal from '../features/admin/components/CreateOrgModal';
import OrgSettingsModal from '../features/admin/components/OrgSettingsModal';
import PageHeader from '../components/PageHeader';
import Spinner from '../components/Spinner';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useNotification } from '../context/useNotification';
import { ADMIN_PAGE_LABELS as LABELS, ROLE_LABELS } from '../utils/labels';
import { isLastFullAccessMember } from '../utils/roles';

interface AdminPageProps {
  orgId: string | null;
}

interface PendingDelete {
  userId: string;
  orgId: string;
}

export default function AdminPage({ orgId }: AdminPageProps) {
  const { orgs, loading, createOrg, removeMember, updateOrgSettings } = useAdminOrgs();
  const { notifySuccess, notifyError } = useNotification();
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [settingsModalOrg, setSettingsModalOrg] = useState<OrgWithMembers | null>(null);

  const handleCreateOrgSave = async (name: string): Promise<boolean> => {
    const ok = await createOrg(name);
    if (ok) notifySuccess(LABELS.createSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleSaveSettings = async (settings: OrgSettingsInput): Promise<boolean> => {
    if (!settingsModalOrg) return false;
    const ok = await updateOrgSettings(settingsModalOrg.id, settings);
    if (ok) notifySuccess(LABELS.limitsSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const { userId, orgId: targetOrgId } = pendingDelete;
    setPendingDelete(null);
    const result = await removeMember(userId, targetOrgId);
    if (result === 'error') notifyError(LABELS.deleteError); else notifySuccess(LABELS.deleteSuccess);
  };

  const handleRemoveClick = (userId: string, org: OrgWithMembers) => {
    if (isLastFullAccessMember(org.members, userId)) {
      notifyError(LABELS.lastFullAccessRemoveBlocked);
      return;
    }
    setPendingDelete({ userId, orgId: org.id });
  };

  const handleSwitch = (id: string) => {
    localStorage.setItem('superadmin_org_id', id);
    window.location.assign('/');
  };

  return (
    <div className="min-h-dvh bg-background">
      <PageHeader />

      <main className="max-w-2xl mx-auto px-4 py-4 md:py-8 flex flex-col gap-8">
        <h1 className="text-lg font-semibold">{LABELS.admin}</h1>
        <div className="flex justify-end">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            {LABELS.newOrgTitle}
          </Button>
        </div>

        <section>
          <p className="text-xs font-medium uppercase tracking-wider text-text-secondary mb-3">
            {LABELS.orgsTitle}
          </p>

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" className="text-primary" />
            </div>
          ) : orgs.length === 0 ? (
            <p className="text-sm text-text-secondary">{LABELS.noOrgs}</p>
          ) : (
            <div className="flex flex-col gap-3">
              {orgs.map((org) => (
                <div
                  key={org.id}
                  data-testid={`org-row-${org.id}`}
                  className="rounded-xl border border-border bg-surface overflow-hidden"
                >
                  <div className="px-4 py-3 flex items-center gap-2 border-b border-border">
                    <span className="font-semibold text-sm flex-1">{org.name}</span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-border-subtle text-text-secondary">
                      {LABELS.memberCount(org.members.length)}
                    </span>
                    <Tooltip title={LABELS.editLimits}>
                      <IconButton
                        size="small"
                        onClick={() => setSettingsModalOrg(org)}
                        aria-label={LABELS.editLimits}
                        data-testid="edit-limits-button"
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {org.id !== orgId && (
                      <Tooltip title={LABELS.switchOrg}>
                        <IconButton
                          size="small"
                          onClick={() => handleSwitch(org.id)}
                          aria-label={LABELS.switchOrg}
                          data-testid="switch-org-button"
                        >
                          <SwapHorizIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>

                  {org.members.length > 0 && (
                    <ul>
                      {org.members.map((m, i) => (
                        <li
                          key={m.user_id}
                          className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="text-sm truncate">{m.email ?? LABELS.noEmail}</span>
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-border-subtle text-text-secondary shrink-0">
                              {ROLE_LABELS[m.role]}
                            </span>
                          </span>
                          <Tooltip title={LABELS.removeMember}>
                            <IconButton
                              onClick={() => handleRemoveClick(m.user_id, org)}
                              aria-label={LABELS.removeMember}
                              color="error"
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {modalOpen && (
        <CreateOrgModal
          onClose={() => setModalOpen(false)}
          onSave={handleCreateOrgSave}
        />
      )}

      {settingsModalOrg && (
        <OrgSettingsModal
          org={settingsModalOrg}
          onClose={() => setSettingsModalOrg(null)}
          onSave={handleSaveSettings}
        />
      )}

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
